import { useCallback, useEffect, useState } from 'react';
import {
  flagsOfPath, loadRepoFlags, publishRepoFlags, repoFlagsState, subscribeRepoFlags, serviceRepo,
} from '../utils/repoFlags';

const FOCUS_MAX_AGE_MS = 30 * 1000;

/**
 * A project's repo flags (utils/repoFlags.js): `{ repos, canChange, terminal, loaded, reload, flagsOf, serviceFlags,
 * publish }`. `flagsOf(path)` is the flags of the repo any project path is in (`services/x/...`, `context/x/...`) or
 * null; `serviceFlags(name)` the same for a service name (`api` or `services/api`). One answer per project is shared
 * by every caller; it is read again when the page gets the focus back. Without a project, or on an older vfs-service:
 * nothing is flagged.
 */
export default function useRepoFlags(projectId) {
  const [state, setState] = useState(() => repoFlagsState(projectId));

  useEffect(() => {
    if (!projectId) { setState(repoFlagsState(null)); return undefined; }
    setState(repoFlagsState(projectId));
    const unsubscribe = subscribeRepoFlags(projectId, setState);
    loadRepoFlags(projectId, { maxAgeMs: FOCUS_MAX_AGE_MS });
    const again = () => { if (document.visibilityState !== 'hidden') { loadRepoFlags(projectId, { maxAgeMs: FOCUS_MAX_AGE_MS }); } };
    window.addEventListener('focus', again);
    return () => { unsubscribe(); window.removeEventListener('focus', again); };
  }, [projectId]);

  const reload = useCallback(() => loadRepoFlags(projectId), [projectId]);
  const publish = useCallback((repos) => publishRepoFlags(projectId, repos), [projectId]);
  const flagsOf = useCallback((path) => flagsOfPath(state.repos, path), [state.repos]);
  const serviceFlags = useCallback((name) => flagsOfPath(state.repos, serviceRepo(name) || ''), [state.repos]);

  return { ...state, reload, publish, flagsOf, serviceFlags };
}
